/**
 * Helper functions for Federal Register API integration
 * Used to find NPRM publication dates and calculate comment deadlines
 */

export interface FederalRegisterDocument {
  document_number: string;
  publication_date: string;
  title: string;
  type: string;
  // FR sometimes returns null for these on PRORULE documents; treat as optional/nullable.
  docket_numbers?: string[] | null;
  agency_names?: string[] | null;
  html_url: string;
  pdf_url: string;
  /** Structured deadline date for the comment period (YYYY-MM-DD), populated for many PRORULEs. */
  comments_close_on?: string | null;
  /**
   * Free-text DATES summary, e.g.
   * "Comments are due on or before May 26, 2026 and reply comments are due on or before June 22, 2026."
   */
  dates?: string | null;
}

export interface FederalRegisterSearchResult {
  count: number;
  total_pages: number;
  results: FederalRegisterDocument[];
}

export interface NPRMMatchResult {
  document: FederalRegisterDocument | null;
  publicationDate: Date | null;
  matchMethod: 'docket' | 'title' | null;
  error?: string;
}

function isDocumentOnOrAfterMinPublicationDate(
  doc: FederalRegisterDocument,
  minPublicationDate?: Date
): boolean {
  if (!minPublicationDate) return true;
  if (!doc?.publication_date) return false;

  const docDate = new Date(doc.publication_date);
  if (Number.isNaN(docDate.getTime())) return false;
  return docDate >= minPublicationDate;
}

/**
 * Search Federal Register API for documents
 */
export async function searchFederalRegister(
  docketNumber: string,
  title?: string
): Promise<FederalRegisterSearchResult> {
  const baseUrl = 'https://www.federalregister.gov/api/v1/documents.json';
  
  // Extract just the docket number (e.g., "25-209" from "WC Docket No. 25-209")
  const docketId = docketNumber.replace(/.*?(\d+-\d+).*?/i, '$1');
  
  // Use conditions[docket_id] parameter format. The FR list endpoint omits some
  // fields (notably `comments_close_on` and `dates`) unless requested explicitly.
  // FR rejects `fields[]=docket_numbers`, so we don't ask for it here — the server
  // is already filtering by docket_id, so any returned doc is tied to that docket.
  const params = new URLSearchParams();
  params.append('conditions[docket_id]', docketId);
  params.append('conditions[agencies][]', 'federal-communications-commission');
  params.append('conditions[type]', 'PRORULE');
  params.append('per_page', '20');
  params.append('order', 'newest');
  for (const field of [
    'document_number',
    'publication_date',
    'title',
    'type',
    'agency_names',
    'html_url',
    'pdf_url',
    'comments_close_on',
    'dates',
  ]) {
    params.append('fields[]', field);
  }

  try {
    const response = await fetch(`${baseUrl}?${params.toString()}`);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('Federal Register API error details:', {
        status: response.status,
        statusText: response.statusText,
        url: `${baseUrl}?${params.toString()}`,
        errorText,
      });
      throw new Error(`Federal Register API error: ${response.status} - ${errorText}`);
    }

    const data: FederalRegisterSearchResult = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching Federal Register by docket:', error);
    
    // If docket search fails and we have a title, try title search
    if (title) {
      return searchFederalRegisterByTitle(title);
    }
    
    throw error;
  }
}

/**
 * Search Federal Register API by title/keywords
 */
export async function searchFederalRegisterByTitle(
  title: string
): Promise<FederalRegisterSearchResult> {
  const baseUrl = 'https://www.federalregister.gov/api/v1/documents.json';
  
  // Extract key words from title (remove common words)
  const keywords = title
    .split(/\s+/)
    .filter(word => word.length > 3)
    .slice(0, 5)
    .join(' ');
  
  // Use conditions[term] for text search and conditions[agencies][] for agency filter
  const params = new URLSearchParams({
    'conditions[term]': keywords,
    'conditions[agencies][]': 'federal-communications-commission',
    'conditions[type]': 'PRORULE',
    per_page: '20',
    order: 'newest',
  });

  try {
    const response = await fetch(`${baseUrl}?${params.toString()}`);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('Federal Register API error details:', {
        status: response.status,
        statusText: response.statusText,
        url: `${baseUrl}?${params.toString()}`,
        errorText,
      });
      throw new Error(`Federal Register API error: ${response.status} - ${errorText}`);
    }

    const data: FederalRegisterSearchResult = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching Federal Register by title:', error);
    throw error;
  }
}

/**
 * Validate if a Federal Register document title matches any of the provided proceeding names
 */
function validateTitleMatch(docTitle: string, proceedingNames: string[]): boolean {
  if (!docTitle || !proceedingNames || proceedingNames.length === 0) {
    return false;
  }

  const docTitleLower = docTitle.toLowerCase();

  // Check if any of the proceeding names match
  for (const proceedingName of proceedingNames) {
    if (!proceedingName) {
      continue;
    }

  const proceedingNameLower = proceedingName.toLowerCase();

  // Skip validation if proceeding name is a filename (contains .pdf or .doc)
  const isProceedingName = !proceedingNameLower.includes('.pdf') && !proceedingNameLower.includes('.doc');
  if (!isProceedingName) {
    return true; // Don't validate filenames
  }

  // Remove common words and normalize
  const removeCommonWords = (str: string) => {
    return str
      .replace(/\b(the|a|an|and|or|but|in|on|at|to|for|of|with|by)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const cleanedProceeding = removeCommonWords(proceedingNameLower);
  const cleanedDoc = removeCommonWords(docTitleLower);

  // Check if one contains the other (for full proceeding names)
  if (cleanedDoc.includes(cleanedProceeding) || cleanedProceeding.includes(cleanedDoc)) {
    return true;
  }

  // Check if they share significant words (at least 50% of proceeding words must match)
  const proceedingWords = cleanedProceeding.split(/\s+/).filter(w => w.length > 3);
  const docWords = cleanedDoc.split(/\s+/).filter(w => w.length > 3);
  const commonWords = proceedingWords.filter(w => docWords.includes(w));

  // Require at least 50% of proceeding words to match, or at least 3 words
  const minMatches = Math.max(3, Math.ceil(proceedingWords.length * 0.5));
    if (commonWords.length >= minMatches) {
      return true;
    }
  }

  return false;
}

/**
 * Find NPRM in Federal Register by matching docket number or title
 */
export async function findNPRMInFederalRegister(
  docketNumber: string,
  nprmTitles?: string[],
  minPublicationDate?: Date
): Promise<NPRMMatchResult> {
  try {
    // Log if multiple titles are provided
    if (nprmTitles && nprmTitles.length > 1) {
      console.log(`Multiple NPRM titles found for docket ${docketNumber}:`, nprmTitles);
    }
    
    // Use first title for search if available (for backward compatibility with searchFederalRegister)
    const firstTitle = nprmTitles && nprmTitles.length > 0 ? nprmTitles[0] : undefined;
    
    // First, try searching by docket number
    const docketResults = await searchFederalRegister(docketNumber, firstTitle);
    const docketDocs = Array.isArray(docketResults?.results) ? docketResults.results : [];

    // Look for exact docket number match
    // When docket matches, prioritize docket match over title validation
    // Only validate title if titles are provided, but don't reject on title mismatch if docket matches
    for (const doc of docketDocs) {
      if (!isDocumentOnOrAfterMinPublicationDate(doc, minPublicationDate)) {
        continue;
      }
      // FR's `conditions[docket_id]=<docket>` already filters server-side, so a returned
      // doc is tied to that docket even when the per-doc `docket_numbers` array is null
      // or empty (common for some PRORULEs, including multi-docket NPRMs). Only enforce
      // the local equality check when FR provides a non-empty array we can validate.
      const docketNumbersArr = Array.isArray(doc.docket_numbers) ? doc.docket_numbers : [];
      const hasMatchingDocket =
        docketNumbersArr.length === 0 ||
        docketNumbersArr.some(
          (docket) => docket && docket.trim().toUpperCase() === docketNumber.trim().toUpperCase()
        );

      if (hasMatchingDocket) {
        // If we have titles, check if any match (but don't reject if none match - docket match is sufficient)
        // This allows matching when Federal Register shows a different title than provided
        if (nprmTitles && nprmTitles.length > 0) {
          const titleMatches = validateTitleMatch(doc.title, nprmTitles);
          if (!titleMatches && nprmTitles.length > 1) {
            console.log(`Docket ${docketNumber} matched, but Federal Register title "${doc.title}" doesn't match any of the provided titles:`, nprmTitles);
            console.log(`Accepting match based on docket number (handling multiple titles case)`);
          } else if (titleMatches && nprmTitles.length > 1) {
            console.log(`Docket ${docketNumber} matched, and Federal Register title "${doc.title}" matches one of the provided titles`);
          }
        }

        const publicationDate = new Date(doc.publication_date);
        return {
          document: doc,
          publicationDate,
          matchMethod: 'docket',
        };
      }
    }

    // If no docket match and we have titles, try title matching
    // Check all results and validate title matches
    if (nprmTitles && nprmTitles.length > 0 && docketDocs.length > 0) {
      for (const doc of docketDocs) {
        if (!isDocumentOnOrAfterMinPublicationDate(doc, minPublicationDate)) {
          continue;
        }
        if (!doc.title || typeof doc.title !== 'string') {
          continue;
        }

        // Validate that the title matches any of the proceeding names
        if (!validateTitleMatch(doc.title, nprmTitles)) {
          continue; // Skip this result and continue searching
        }

        // Title matches, return this result
        if (nprmTitles.length > 1) {
          console.log(`Title match found for docket ${docketNumber}: Federal Register title "${doc.title}" matches one of the provided titles:`, nprmTitles);
        }
        const publicationDate = new Date(doc.publication_date);
        return {
          document: doc,
          publicationDate,
          matchMethod: 'title',
        };
      }
    }

    // If still no match and we have titles, try searching by each title
    // Check all results and validate title matches
    if (nprmTitles && nprmTitles.length > 0) {
      for (const title of nprmTitles) {
      try {
          const titleResults = await searchFederalRegisterByTitle(title);
          const titleDocs = Array.isArray(titleResults?.results) ? titleResults.results : [];

        // Check all results, not just the first one
        for (const doc of titleDocs) {
            if (!isDocumentOnOrAfterMinPublicationDate(doc, minPublicationDate)) {
              continue;
            }
            // Validate that the title matches any of the proceeding names
            if (!validateTitleMatch(doc.title, nprmTitles)) {
            continue; // Skip this result and continue searching
          }

            // Verify it's likely the same NPRM by checking docket numbers when present.
            // (Title-search responses don't carry a server-side docket filter, so this is
            // intentionally stricter than the docket-id search path above.)
            const hasMatchingDocket =
              !!doc.docket_numbers &&
              Array.isArray(doc.docket_numbers) &&
              doc.docket_numbers.some(
                (docket) => docket && docket.trim().toUpperCase() === docketNumber.trim().toUpperCase()
              );
            
            if (nprmTitles.length > 1) {
              console.log(`Title search match found for docket ${docketNumber}: Federal Register title "${doc.title}" matches one of the provided titles:`, nprmTitles);
            }
            const publicationDate = new Date(doc.publication_date);
            return {
              document: doc,
              publicationDate,
              matchMethod: hasMatchingDocket ? 'docket' : 'title',
            };
        }
      } catch (titleError) {
          // Title search failed, continue to next title
        }
      }
    }

    // No match found
    return {
      document: null,
      publicationDate: null,
      matchMethod: null,
      error: 'NPRM not found in Federal Register',
    };
  } catch (error) {
    // Downgraded from console.error: the happy "not found" path now returns a
    // structured result without throwing, so anything reaching here is either a
    // network hiccup or an unexpected API response shape — worth a warn, not an error.
    console.warn('Federal Register NPRM lookup failed:', error);
    return {
      document: null,
      publicationDate: null,
      matchMethod: null,
      error: error instanceof Error ? error.message : 'Failed to search Federal Register',
    };
  }
}

/**
 * Normalize docket number for comparison
 */
export function normalizeDocketNumber(docketNumber: string): string {
  return docketNumber.trim().toUpperCase().replace(/\s+/g, '');
}
